import React, { useState } from 'react';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { LogOut, User, Building, Package, FileText, Scissors, Menu, X, Crown } from 'lucide-react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import UnifiedLogin from '../../pages/UnifiedLogin';
import productexLogo from './productex-logo.png';

const ClienteLayout: React.FC = () => {
  const { cliente, logout } = useClienteAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/cliente');
  };

  const navigation = [
    { name: 'Cuenta', href: '/cliente', icon: FileText },
    { name: 'Club Productex®', href: '/cliente/club', icon: Crown },
    { name: 'Express', href: '/cliente/pedidos', icon: Package },
    { name: 'Muestras', href: '/cliente/muestras', icon: Scissors },
    { name: 'Producciones', href: '/cliente/producciones', icon: Building },
    { name: 'Mi Perfil', href: '/cliente/perfil', icon: User },
  ];

  if (!cliente) {
    return <UnifiedLogin />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Barra superior negra con navegación */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black text-white">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={productexLogo}
                alt="Productex"
                className="h-[1.2rem] w-auto object-contain"
              />
            </div>

            {/* Navegación desktop */}
            <nav className="hidden md:flex items-center space-x-1">
              {navigation.map((item, index) => {
                const isActive = location.pathname === item.href;
                const showSeparatorAfter = index === 1 || index === 4;
                return (
                  <React.Fragment key={item.name}>
                    <Link
                      to={item.href}
                      className={`px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center space-x-2 ${isActive ? 'bg-white text-black' : 'text-white hover:bg-white/10'}`}
                    >
                      <item.icon className={`h-4 w-4 ${isActive ? 'text-black' : 'text-white'}`} />
                      <span>{item.name}</span>
                    </Link>
                    {showSeparatorAfter && (
                      <div className="h-5 w-px bg-white/30 mx-1" aria-hidden="true" />
                    )}
                  </React.Fragment>
                );
              })}
            </nav>

            {/* Acciones derecha */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Abrir menú"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              <div className="hidden sm:flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-xs font-medium leading-4">{cliente.nombre}</p>
                  {cliente.empresa && (
                    <p className="text-[10px] text-gray-300">{cliente.empresa}</p>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center p-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                  title="Salir"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Menú móvil desplegable */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-3 border-t border-white/10">
              <nav className="flex flex-col py-2">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  const showSeparatorAfter = index === 1 || index === 4;
                  return (
                    <React.Fragment key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 ${isActive ? 'bg-white text-black' : 'text-white hover:bg-white/10'}`}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.name}</span>
                      </Link>
                      {showSeparatorAfter && (
                        <div className="h-px bg-white/20 mx-3 my-1" aria-hidden="true" />
                      )}
                    </React.Fragment>
                  );
                })}
                <button
                  onClick={handleLogout}
                  className="mt-2 flex items-center w-full justify-between px-3 py-2 text-left rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                >
                  <span>Salir</span>
                  <LogOut className="h-4 w-4" />
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Contenedor principal con tarjeta blanca redondeada */}
      <main className="pt-16 px-3 sm:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 lg:p-8">
              <Outlet />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClienteLayout;
